import { AnimatePresence, motion, type Transition } from "framer-motion";
import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode
} from "react";
import type { MatchProps } from "./Match";

type AnimationType = "slide" | "fade" | "none";
type Direction = "forward" | "backward";

interface AnimatedSwitchProps {
  /** Content to render when no `Match` conditions are truthy. */
  fallback?: ReactNode;
  /** `Match` components defining the cases to evaluate. */
  children?: ReactNode;
  /**
   * Animation type for transitions between matches.
   * - `slide`: iOS-style horizontal slide (default)
   * - `fade`: Simple fade transition
   * - `none`: No animation
   */
  animation?: AnimationType;
  /**
   * Current step index for direction inference.
   * When provided, the component tracks direction automatically:
   * - Higher index = forward (slide from right)
   * - Lower index = backward (slide from left)
   *
   * In value mode, this is auto-inferred from the matched child's
   * position unless explicitly provided.
   */
  stepIndex?: number;
  /**
   * Explicit direction override. Takes precedence over stepIndex inference.
   * - `forward`: New content slides in from right
   * - `backward`: New content slides in from left
   */
  direction?: Direction;
  /** Custom transition timing. Defaults to iOS-like spring. */
  transition?: Transition;
  /** Additional style for the motion wrapper. */
  style?: CSSProperties;
  /**
   * Whether to scroll the container into view on step change.
   * Set to `false` for nested AnimatedSwitch instances.
   * @default false
   */
  scrollOnChange?: boolean;
  /** Sets `height: 100%` on the container and motion wrapper. */
  fillHeight?: boolean;
  /**
   * Value to match against `Match` children's `case` prop.
   * When provided, renders the first `Match` whose `case === value`.
   * Also auto-infers `stepIndex` from the matched child's position.
   *
   * @example
   * <AnimatedSwitch value={step}>
   *   <Match case="intro"><IntroStep /></Match>
   *   <Match case="form"><FormStep /></Match>
   * </AnimatedSwitch>
   */
  value?: unknown;
}

const defaultTransition: Transition = {
  type: "tween",
  duration: 0.68,
  ease: [0.32, 0.72, 0, 1]
};

const posXout = "max(-20%, -100px)";
const posXin = "min(40%, 200px)";

const slideVariants = {
  enter: (direction: Direction) => ({
    x: direction === "forward" ? posXin : posXout,
    opacity: 0,
    zIndex: 90
  }),
  center: {
    x: 0,
    opacity: 1,
    zIndex: 90
  },
  exit: (direction: Direction) => ({
    x: direction === "forward" ? posXout : posXin,
    opacity: 0,
    zIndex: 80
  })
};

const fadeVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 }
};

/**
 * Animated version of Switch that provides smooth transitions between matches.
 *
 * Renders the first `Match` child whose `when` condition is truthy,
 * with configurable animation between state changes.
 *
 * @example
 * // Value-based matching with auto direction inference
 * <AnimatedSwitch value={step}>
 *   <Match case="intro"><IntroStep /></Match>
 *   <Match case="form"><FormStep /></Match>
 * </AnimatedSwitch>
 *
 * @example
 * // Fade animation
 * <AnimatedSwitch animation="fade">
 *   <Match when={isLoading}><Spinner /></Match>
 *   <Match when={data}>{(data) => <Content data={data} />}</Match>
 * </AnimatedSwitch>
 *
 * @see {@link Switch} for non-animated version
 * @see {@link Match} for defining individual cases
 */
function AnimatedSwitch({
  fallback,
  children,
  animation = "slide",
  stepIndex: stepIndexProp,
  direction: explicitDirection,
  transition = defaultTransition,
  style,
  scrollOnChange = false,
  fillHeight,
  value
}: AnimatedSwitchProps): ReactNode {
  const prevStepIndexRef = useRef<number | undefined>(undefined);
  const directionRef = useRef<Direction>("forward");
  const containerRef = useRef<HTMLDivElement>(null);
  const nextPreloadRef = useRef<(() => void) | undefined>(undefined);

  const handleAnimationStart = useCallback(
    (definition: string) => {
      if (scrollOnChange && definition === "center" && containerRef.current) {
        containerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    },
    [scrollOnChange]
  );

  const isValueMode = value !== undefined;

  // Find the first matching child
  const childArray = Children.toArray(children);
  let matchedContent: ReactNode = null;
  let matchKey: string | undefined;
  let matchedIndex: number | undefined;

  for (let i = 0; i < childArray.length; i++) {
    const child = childArray[i];
    if (!isValidElement<MatchProps<unknown>>(child)) {
      continue;
    }

    const isMatch = isValueMode
      ? child.props.case === value
      : !!child.props.when;

    if (!isMatch) {
      continue;
    }

    const { when, children: matchChildren } = child.props;
    matchKey = `match-${i}`;
    matchedIndex = i;

    if (typeof matchChildren === "function") {
      matchedContent = matchChildren(isValueMode ? value : when);
    } else {
      matchedContent = matchChildren;
    }

    // Check next Match for preload
    const nextChild = childArray[i + 1];
    if (
      isValidElement<MatchProps<unknown>>(nextChild) &&
      nextChild.props.preload
    ) {
      nextPreloadRef.current = nextChild.props.preload;
    } else {
      nextPreloadRef.current = undefined;
    }

    break;
  }

  // Use fallback if no match
  if (matchedContent === null) {
    matchedContent = fallback ?? null;
    matchKey = "fallback";

    // If no match, check if the first child has a preload
    const firstChild = childArray[0];
    if (
      isValidElement<MatchProps<unknown>>(firstChild) &&
      firstChild.props.preload
    ) {
      nextPreloadRef.current = firstChild.props.preload;
    } else {
      nextPreloadRef.current = undefined;
    }
  }

  // In value mode, auto-infer stepIndex from matched child position
  const stepIndex = stepIndexProp ?? (isValueMode ? matchedIndex : undefined);

  // Determine direction from stepIndex changes or explicit prop
  if (explicitDirection) {
    directionRef.current = explicitDirection;
  } else if (
    stepIndex !== undefined &&
    prevStepIndexRef.current !== undefined &&
    stepIndex !== prevStepIndexRef.current
  ) {
    directionRef.current =
      stepIndex > prevStepIndexRef.current ? "forward" : "backward";
  }

  // Update ref after direction calculation
  if (stepIndex !== prevStepIndexRef.current) {
    prevStepIndexRef.current = stepIndex;
  }

  const direction = directionRef.current;

  // Call preload of the next Match after current match renders + delay
  useEffect(() => {
    const preload = nextPreloadRef.current;
    if (!preload) {
      return;
    }
    const timer = setTimeout(preload, 500);
    return () => clearTimeout(timer);
  }, [matchKey]);

  if (animation === "none") {
    return matchedContent;
  }

  const isSlide = animation === "slide";

  if (isSlide) {
    return (
      <div
        ref={containerRef}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gridTemplateRows: "1fr",
          width: "100%",
          overflow: "hidden",
          ...(fillHeight && { height: "100%" }),
          ...style
        }}
        className={"animated-switch-container"}
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={matchKey}
            className={"animated-switch-slide"}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={transition}
            onAnimationStart={handleAnimationStart}
            style={{
              gridArea: "1 / 1",
              width: "100%",
              overflow: "hidden",
              ...(fillHeight && { height: "100%" })
            }}
          >
            {matchedContent}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={matchKey}
        variants={fadeVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={transition}
        className={"animated-switch-fade"}
        style={{
          width: "100%",
          ...(fillHeight && { height: "100%" }),
          ...style
        }}
      >
        {matchedContent}
      </motion.div>
    </AnimatePresence>
  );
}

export default AnimatedSwitch;
